// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useEffect, useRef, useState } from 'react';



// ----------------------------------------------------------------------------
//   Fullscreen Logic
//   Adapted from https://stackoverflow.com/a/23971798
// ----------------------------------------------------------------------------

export async function requestFullScreen(element) {
  if (element.requestFullscreen)
    await element.requestFullscreen();
  else if (element.msRequestFullscreen)
    await element.msRequestFullscreen();
  else if (element.mozRequestFullScreen)
    await element.mozRequestFullScreen();
  else if (element.webkitRequestFullscreen)
    await element.webkitRequestFullscreen();
}

export async function exitFullScreen() {
  if (document.exitFullscreen)
    await document.exitFullscreen();
  else if (document.msExitFullscreen)
    await document.msExitFullscreen();
  else if (document.mozCancelFullScreen)
    await document.mozCancelFullScreen();
  else if (document.webkitExitFullscreen)
    await document.webkitExitFullscreen();
}



// ----------------------------------------------------------------------------
//   React Hook
// ----------------------------------------------------------------------------

/**
 * Manage the fullscreen state of an element.
 */
export function useFullscreenElement( initialRef ) {

  // Reference to the element that should be fullscreened
  const fullscreenRef = useRef( initialRef );

  // Track the fullscreen state
  const [ isFullscreen, setIsFullscreen ] = useState(false);

  // If the fullscreen state is changed externally (e.g. using the ESC key),
  // keep the internal state consistent
  const fullscreenHandler = () => {

    // Make sure there's a target
    if (!fullscreenRef.current) return;

    // If fullscreen is exiting, or the fullscreened element is different, set the state to false
    if (!document.fullscreenElement || (document.fullscreenElement !== fullscreenRef.current) ) {
      setIsFullscreen( false );
    }

    // Otherwise, set the state to true
    else {
      setIsFullscreen( true );
    }
  }

  // If the fullscreen state is changed externally (e.g. using the ESC key),
  // keep the internal state consistent
  // https://stackoverflow.com/questions/74825788/fullscreenchange-event-browser-support ??
  useEffect(() => {

    // Make sure there's a target
    if (!fullscreenRef.current) return;

    // Add the handler to the current fullscreen target reference
    fullscreenRef.current.addEventListener('fullscreenchange', fullscreenHandler);

    // Remove the event listener when effect changes
    return () => {
      if (!fullscreenRef.current) return;
      fullscreenRef.current.removeEventListener('fullscreenchange', fullscreenHandler);
    }

  }, [fullscreenRef])

  // Return objects
  return [

    // State & ref
    isFullscreen,
    fullscreenRef,

    // Toggle to the given state, if possible
    async ( newState ) => {

      // Make sure target element defined
      if (!fullscreenRef.current) {
        throw new Error("Can't fullscreen - no target element specified.");
      }

      // Request fullscreen on the given element
      if ( newState ) {
        await requestFullScreen( fullscreenRef.current );
      }

      // Exit fullscreen
      else {
        await exitFullScreen();
      }
    },
  ]
}
